import { DEFAULT_PERSONA } from '@/features/decisions/config/personaContext';
import type { FcfEntitlementCards } from '@/features/decisions/decisions.api.types';
import { fmtDateRange, fmtInteger, fmtMoneyM, weeksBetween } from '@/features/decisions/utils/fcfFormat';

interface ValueOpportunityKpisProps {
  cards: FcfEntitlementCards | undefined;
  // Δ Operating Cash Flow (Optimized − Incumbent) at the shared lost-sales
  // assumption — same value as the chart's Optimized bar and the Build-Up total.
  fcfOpportunity: number | null;
  lostSalesPct: number;
  periodFrom: string;
  periodTo: string;
  chartMultiplier?: number;
}

export function ValueOpportunityKpis({
  cards,
  fcfOpportunity,
  lostSalesPct,
  periodFrom,
  periodTo,
  chartMultiplier = DEFAULT_PERSONA.chartMultiplier,
}: ValueOpportunityKpisProps) {
  const periodFromResolved = cards?.period_from ?? periodFrom;
  const rawWeeks = weeksBetween(periodFromResolved, cards?.period_to ?? periodTo);
  const weeks = rawWeeks != null ? Math.round(rawWeeks * chartMultiplier) : null;

  // Extend the end date by the same factor so the sub-text date range matches the scaled window.
  const scaledPeriodTo = (() => {
    if (!periodFromResolved || rawWeeks == null) return cards?.period_to ?? periodTo;
    const startMs = new Date(`${periodFromResolved}T00:00:00Z`).getTime();
    const endMs = startMs + Math.round(rawWeeks * chartMultiplier) * 7 * 24 * 60 * 60 * 1000;
    return new Date(endMs).toISOString().slice(0, 10);
  })();

  return (
    <div className="grid grid-cols-1 divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white md:grid-cols-4 md:divide-x md:divide-y-0">
      <KpiSlot
        label="FCF Opportunity"
        value={fmtMoneyM(fcfOpportunity, 1)}
        valueTone={fcfOpportunity != null && fcfOpportunity >= 0 ? 'emerald' : 'rose'}
        sub={`vs. Incumbent · lost sales ${Math.round(lostSalesPct * 100)}%`}
        source="Δ Operating Cash Flow vs Incumbent (Cash In − Production Cost)"
      />
      <KpiSlot
        label="SKUs Evaluated"
        value={fmtInteger(cards?.skus_evaluated)}
        sub="Active replenished SKUs"
        source="cards.skus_evaluated"
      />
      <KpiSlot
        label="Period"
        value={weeks != null ? `${weeks}w window` : 'Backtest window'}
        sub={fmtDateRange(periodFromResolved, scaledPeriodTo)}
        source="cards.period_from … cards.period_to"
      />
      <KpiSlot
        label="Methodology"
        value="Backtest"
        sub={`${cards?.policy_count ?? '—'} policies replayed vs. actuals`}
        source="cards.policy_count"
      />
    </div>
  );
}

function KpiSlot({
  label,
  value,
  sub,
  source,
  valueTone = 'default',
}: {
  label: string;
  value: string;
  sub?: string;
  source?: string;
  valueTone?: 'default' | 'emerald' | 'rose';
}) {
  const tone = valueTone === 'emerald' ? 'text-emerald-600' : valueTone === 'rose' ? 'text-rose-600' : 'text-gray-900';
  return (
    <div className="p-4">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-1 text-3xl font-bold tabular-nums ${tone}`} title={source}>
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
    </div>
  );
}
